import React, { useState } from 'react';
import { Card, Row, Col, Statistic, Table, Select } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart, PieChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([BarChart, PieChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const COLORS = {
  brand: '#00A651',
  brandYellow: '#FFD100',
  blue: '#1890ff',
  purple: '#722ed1',
  orange: '#ff7a45',
  success: '#52C41A',
};

/**
 * Module 5: Financial & Trends
 * Channel comparison + Cash reconciliation + Payment breakdown
 */
const FinancialTrends = ({ data }) => {
  const [cashFilter, setCashFilter] = useState('all');

  if (!data) return null;

  // Grouped bar: Channel sales comparison
  const channelOption = {
    tooltip: { trigger: 'axis' },
    grid: { left: 60, right: 20, top: 20, bottom: 30 },
    xAxis: {
      type: 'category',
      data: ["Lotus's", 'Amaze', 'Cpxtra mall'],
    },
    yAxis: { type: 'value', name: 'THB', axisLabel: { formatter: (v) => `${(v / 1000).toFixed(0)}K` } },
    series: [{
      type: 'bar',
      data: [
        { value: data.lotusSales, itemStyle: { color: COLORS.brand } },
        { value: data.amazeSales, itemStyle: { color: COLORS.purple } },
        { value: data.cpxtraMallSales, itemStyle: { color: COLORS.orange } },
      ],
      barWidth: 40,
      label: { show: true, position: 'top', formatter: (p) => `${(p.value / 1000).toFixed(0)}K`, fontWeight: 'bold' },
      itemStyle: { borderRadius: [4, 4, 0, 0] },
    }],
  };

  // Payment method pie
  const paymentPieOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} THB ({d}%)' },
    legend: { bottom: 0, textStyle: { fontSize: 11 } },
    series: [{
      type: 'pie',
      radius: ['35%', '65%'],
      center: ['50%', '42%'],
      label: { show: true, formatter: '{b}\n{d}%', fontSize: 11 },
      data: [
        { value: data.digitalWallet, name: 'Digital Wallet', itemStyle: { color: COLORS.brand } },
        { value: data.creditCard, name: 'Credit Card', itemStyle: { color: COLORS.blue } },
        { value: data.eWallet, name: 'E-Wallet', itemStyle: { color: COLORS.purple } },
      ],
    }],
  };

  // Cash drill-down table columns
  const cashColumns = [
    { title: 'Rider', dataIndex: 'rider', key: 'rider' },
    { title: 'Channel', dataIndex: 'channel', key: 'channel' },
    { title: 'POS No', dataIndex: 'posNo', key: 'posNo' },
    {
      title: 'Delivering cash',
      dataIndex: 'delivering',
      key: 'delivering',
      render: (v) => <span style={{ color: COLORS.orange }}>THB {v.toLocaleString()}</span>,
    },
    {
      title: 'Delivered cash',
      dataIndex: 'delivered',
      key: 'delivered',
      render: (v) => <span style={{ color: COLORS.success }}>THB {v.toLocaleString()}</span>,
    },
    {
      title: 'Total',
      key: 'total',
      render: (_, row) => <b>THB {(row.delivering + row.delivered).toLocaleString()}</b>,
    },
  ];

  // Generate rider filter options from data
  const riderOptions = [
    { label: 'All Riders', value: 'all' },
    ...(data.cashOrdersByRider || []).map((row) => ({
      label: row.rider,
      value: row.rider,
    })),
  ];

  const filteredCashData = cashFilter === 'all'
    ? (data.cashOrdersByRider || [])
    : (data.cashOrdersByRider || []).filter((row) => row.rider === cashFilter);

  return (
    <Card title="Channel & Financial Analysis" styles={{ body: { padding: 16 } }}>
      <Row gutter={16}>
        {/* Channel comparison */}
        <Col span={8}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13, textAlign: 'center' }}>Channel Sales Comparison</div>
          <ReactEChartsCore echarts={echarts} option={channelOption} style={{ height: 280 }} opts={{ renderer: 'canvas' }} />
        </Col>

        {/* Online payment pie */}
        <Col span={8}>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13, textAlign: 'center' }}>Online Payment Methods</div>
          <ReactEChartsCore echarts={echarts} option={paymentPieOption} style={{ height: 280 }} opts={{ renderer: 'canvas' }} />
        </Col>

        {/* Cash Reconcile Panel */}
        <Col span={8}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              <DollarOutlined style={{ color: COLORS.brandYellow, marginRight: 4 }} />
              Cash on Delivery Reconciliation
            </div>
            <Select
              size="small"
              value={cashFilter}
              onChange={setCashFilter}
              style={{ width: 120 }}
              options={riderOptions}
            />
          </div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <Card style={{ flex: 1, background: '#fffbe6', border: '1px solid #ffe58f' }} styles={{ body: { padding: 12 } }}>
              <Statistic
                title={<span style={{ fontSize: 12 }}>Total Cash Amount</span>}
                value={data.cashOnDelivery}
                prefix="THB"
                valueStyle={{ fontSize: 22, fontWeight: 'bold', color: '#d48806' }}
              />
            </Card>
          </div>
          <Table
            columns={cashColumns}
            dataSource={filteredCashData}
            rowKey="rider"
            pagination={false}
            size="small"
            scroll={{ x: 'max-content', y: 200 }}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default FinancialTrends;
